import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Card, 
  CardContent,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import DownloadIcon from '@mui/icons-material/Download';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ScaleIcon from '@mui/icons-material/Scale';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import html2pdf from 'html2pdf.js';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import {
  CUSTOMER_INFO_MAP,
  resolveCustomerIdFromUserDoc,
  type CustomerId,
  CUSTOMER_COMPANY_MAP
} from '../config/customerConfig';
import { getCustomerCache, setCustomerCache } from '../utils/customerCache';

// 화주 정보 타입
interface CustomerInfo {
  name: string;
  logoPath: string;
  color: string;
  backgroundColor: string;
  lightColor: string;
}

// 통계 카드 컴포넌트
const StatCard = ({ 
  title, 
  value, 
  unit, 
  change, 
  icon, 
  color,
  isIncrease 
}: {
  title: string;
  value: number | string;
  unit: string;
  change?: number;
  icon: React.ReactNode;
  color: string;
  isIncrease?: boolean;
}) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  
  // 모바일에서 제목 축약
  const getMobileTitle = (title: string) => {
    const titleMap: { [key: string]: string } = {
      '총 운송 건수': '운송 건수',
      '총 운송량': '운송량',
      '총 운송료': '운송료',
      '총 운송 거리': '운송 거리'
    };
    return isMobile ? (titleMap[title] || title) : title;
  };

  return (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: `${color}20`,
            color: color,
            mr: 2
          }}>
            {icon}
          </Box>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              fontSize: isMobile ? 12 : 14,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {getMobileTitle(title)}
          </Typography>
        </Box>
        
        <Typography variant="h4" fontWeight={700} sx={{ mb: 1, color: '#333' }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
          <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1 }}>
            {unit}
          </Typography>
        </Typography>
        
        {change !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isIncrease ? (
              <TrendingUpIcon sx={{ color: '#f44336', fontSize: 16, mr: 0.5 }} />
            ) : (
              <TrendingDownIcon sx={{ color: '#1976d2', fontSize: 16, mr: 0.5 }} />
            )}
            <Typography 
              variant="body2" 
              sx={{ 
                color: isIncrease ? '#f44336' : '#1976d2',
                fontWeight: 600 
              }}
            >
              {isIncrease ? '+' : ''}{change}%
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              전월 대비
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const CustomerReportPage = ({
  showStatsInReport,
  reportCompanyName,
}: {
  showStatsInReport?: boolean;
  /** CustomerPage `currentCompany` — BigQuery/배차와 동일 회사 키 (있으면 CUSTOMER_COMPANY_MAP보다 우선) */
  reportCompanyName?: string;
}) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [user, setUser] = useState<User | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  
  // 날짜 선택 상태
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // 데이터 상태
  const [reportData, setReportData] = useState<any>(null);
  const [dailyData, setDailyData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [resolvedCustomerId, setResolvedCustomerId] = useState<CustomerId | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser?.email) {
        const userSnap = await getDoc(doc(db, 'users', currentUser.email));
        const cidField = userSnap.exists() ? userSnap.data()?.customerId : undefined;
        const customerId = resolveCustomerIdFromUserDoc(currentUser.email, cidField);
        setResolvedCustomerId(customerId);
        const info = CUSTOMER_INFO_MAP[customerId];
        setCustomerInfo({
          name: info.name,
          logoPath: info.logoPath,
          color: info.color,
          backgroundColor: info.backgroundColor,
          lightColor: info.lightColor
        });
      } else {
        setResolvedCustomerId(null);
      }
    });
    return unsubscribe;
  }, []);

  // 통합 통계 API로 데이터 가져오기
  const fetchSummaryData = async () => {
    if (!user?.email || !resolvedCustomerId) return;
    setLoading(true);
    setError(null);
    try {
      const fromParent = (reportCompanyName || '').trim();
      const companyName = fromParent || CUSTOMER_COMPANY_MAP[resolvedCustomerId];
      if (!companyName) throw new Error('화주 정보를 찾을 수 없습니다');
      // const cacheKey = `customer-report-${companyName}-${selectedYear}-${selectedMonth}`;
      // const ttl = 24 * 60 * 60 * 1000; // 1일
      // const cached = getCustomerCache<{monthly: any, daily: any[]}>(cacheKey, ttl);
      // if (cached) {
      //   setReportData(cached.monthly || null);
      //   setDailyData(cached.daily || []);
      //   setLoading(false);
      //   return;
      // }
      const res = await fetch(`/api/bigquery-dashboard/customer/summary/${encodeURIComponent(companyName)}?year=${selectedYear}&month=${selectedMonth.toString().padStart(2, '0')}`);
      if (!res.ok) throw new Error('통계 데이터를 불러오지 못했습니다');
      const data = await res.json();
      setReportData(data.monthly || null);
      setDailyData(data.daily || []);
      // setCustomerCache(cacheKey, { monthly: data.monthly, daily: data.daily }, ttl);
    } catch (err: any) {
      setError(err.message);
      setReportData(null);
      setDailyData([]);
    } finally {
      setLoading(false);
    }
  };

  // 데이터 로드 (사용자 정보나 날짜가 변경될 때)
  useEffect(() => {
    if (user?.email && resolvedCustomerId) {
      fetchSummaryData();
    }
  }, [user?.email, resolvedCustomerId, selectedYear, selectedMonth, reportCompanyName]);

  // 샘플 데이터 제거하고 실제 데이터 사용
  const monthlyStats = reportData ? {
    totalOrders: reportData.totalOrders,
    totalWeight: reportData.totalWeight,
    totalAmount: reportData.totalAmount,
    totalDistance: reportData.totalDistance, // 차량 활용도 대신 총 거리 사용
    previousMonthChange: {
      orders: reportData.changes.orders,
      weight: reportData.changes.weight,
      amount: reportData.changes.amount,
      distance: reportData.changes.distance
    }
  } : {
    totalOrders: 0,
    totalWeight: 0,
    totalAmount: 0,
    totalDistance: 0,
    previousMonthChange: {
      orders: 0,
      weight: 0,
      amount: 0,
      distance: 0
    }
  };

  // 차종별 데이터 (실제 데이터 사용) - 상위 4개 항목만 표시, 나머지는 기타로 묶음
  const vehicleTypeData = (() => {
    if (!reportData?.vehicleTypes || reportData.vehicleTypes.length === 0) {
      return [];
    }

    // 건수 기준으로 내림차순 정렬
    const sortedTypes = [...reportData.vehicleTypes].sort((a, b) => b.count - a.count);
    
    // 상위 4개 항목
    const topItems = sortedTypes.slice(0, 4);
    
    // 나머지 항목들의 합계
    const remainingItems = sortedTypes.slice(4);
    const etcCount = remainingItems.reduce((sum, item) => sum + item.count, 0);
    
    // 결과 배열 생성
    const result = topItems.map((item, index) => ({
      name: item.type,
      value: item.count,
      color: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'][index]
    }));
    
    // 기타 항목이 있으면 추가
    if (etcCount > 0) {
      result.push({
        name: '기타',
        value: etcCount,
        color: '#FFEAA7'
      });
    }
    
    return result;
  })();

  // 년도 선택: 항상 현재 연도까지 최근 3년을 자동으로 생성
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear];
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  const handleDownloadPDF = async () => {
    if (pdfGenerating) return;
    
    // 데이터 유효성 검사
    if (!reportData && dailyData.length === 0) {
      alert('PDF 생성에 필요한 데이터가 없습니다. 데이터를 불러온 후 다시 시도해주세요.');
      return;
    }
    
    setPdfGenerating(true);
    try {
      // 1. 웹 페이지에서 차트들을 캔버스로 변환
      console.log('차트 변환 중...');
      const dailyChartCanvas = await convertChartToCanvas('daily-chart');
      const vehicleChartCanvas = await convertChartToCanvas('vehicle-chart');
      
      // 차트 변환 결과 검증
      if (!dailyChartCanvas) {
        console.warn('일별 차트 변환에 실패했습니다. 기본 메시지로 대체됩니다.');
      }
      if (!vehicleChartCanvas) {
        console.warn('차종별 차트 변환에 실패했습니다. 기본 메시지로 대체됩니다.');
      }
      
      // 2. 차트 데이터를 이미지로 변환
      const dailyChartImage = dailyChartCanvas ? dailyChartCanvas.toDataURL('image/png') : '';
      const vehicleChartImage = vehicleChartCanvas ? vehicleChartCanvas.toDataURL('image/png') : '';

      // 3. PDF 생성용 HTML 템플릿
      const element = document.createElement('div');
      element.innerHTML = `
        <div style="position: relative; min-height: 1123px; padding: 10px; font-family: 'Noto Sans KR', sans-serif; background: white; line-height: 1.3; font-size: 11px; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; text-rendering: optimizeLegibility;">
          <!-- 헤더 - 로고, 제목, 슬로건을 한 줄에 배치 -->
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 2px solid ${customerInfo?.color || '#1976d2'};">
            <img src="/logo.png" alt="SW Logo" style="height: 48px;" />
            <div style="text-align: center; flex: 1;">
              <h1 style="color: #444444; font-size: 18px; font-weight: 700; margin: 0;">
                ${selectedYear}년 ${selectedMonth}월 운송 현황 보고서
              </h1>
            </div>
            <img src="/slogan.png" alt="Your Cargo, Our Care" style="height: 28px;" />
          </div>

          <!-- 고객사 정보 - 한 줄로 배치 -->
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px; padding: 8px 0; border-bottom: 1px solid #e0e0e0;">
            <div style="font-size: 14px;"><strong>고객사:</strong> ${customerInfo?.name || ''}</div>
            <div style="font-size: 14px;"><strong>제출일:</strong> ${new Date().toLocaleDateString('ko-KR')}</div>
          </div>

          <!-- 기간별 비교 -->
          <div style="margin-bottom: 15px;">
            <div style="background: #f8f9fa; padding: 10px; border-radius: 4px; border-left: 4px solid ${customerInfo?.color || '#1976d2'};">
              <h3 style="color: #333; font-size: 14px; margin: 0 0 8px 0;">• 기간</h3>
              <div style="display: flex; justify-content: space-between;">
                <div>
                  <div style="font-weight: 600; margin-bottom: 6px;">${selectedYear}. ${selectedMonth}</div>
                  <div style="margin-bottom: 3px;">총 운행 건수 :&nbsp;&nbsp;${(monthlyStats.totalOrders || 0).toLocaleString()} 건 (전월대비 ${(monthlyStats.previousMonthChange.orders || 0) > 0 ? '+' : ''}${(monthlyStats.previousMonthChange.orders || 0).toFixed(1)}%)</div>
                  <div style="margin-bottom: 3px;">총 운송 중량 :&nbsp;&nbsp;${(monthlyStats.totalWeight || 0).toLocaleString()} Ton (전월대비 ${(monthlyStats.previousMonthChange.weight || 0) > 0 ? '+' : ''}${(monthlyStats.previousMonthChange.weight || 0).toFixed(1)}%)</div>
                  <div style="margin-bottom: 3px;">총 운송 거리 :&nbsp;&nbsp;${(monthlyStats.totalDistance || 0).toLocaleString()} km (전월대비 ${(monthlyStats.previousMonthChange.distance || 0) > 0 ? '+' : ''}${(monthlyStats.previousMonthChange.distance || 0).toFixed(1)}%)</div>
                </div>
                <div>
                  <div style="font-weight: 600; margin-bottom: 6px;">전월</div>
                  <div style="margin-bottom: 3px;">총 운행 건수 :&nbsp;&nbsp;${(reportData?.prevOrders || 0).toLocaleString()} 건</div>
                  <div style="margin-bottom: 3px;">총 운송 중량 :&nbsp;&nbsp;${(reportData?.prevWeight || 0).toLocaleString()} Ton</div>
                  <div style="margin-bottom: 3px;">총 운송 거리 :&nbsp;&nbsp;${(reportData?.prevDistance || 0).toLocaleString()} km</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 일별 운송 현황 차트 -->
          <div style="margin-bottom: 15px;">
            <h3 style="color: #333; font-size: 14px; margin-bottom: 8px; border-bottom: 2px solid ${customerInfo?.color || '#1976d2'}; padding-bottom: 4px;">• 일별 운송 현황</h3>
            <div style="display: flex; margin-bottom: 8px;">
              <div style="margin-right: 25px;">최대 :&nbsp;&nbsp;${dailyData.length > 0 ? Math.max(...dailyData.map(d => d.orders || 0)) : 0} 건</div>
              <div style="margin-right: 25px;">평균 :&nbsp;&nbsp;${dailyData.length > 0 ? Math.floor(dailyData.reduce((sum, d) => sum + (d.orders || 0), 0) / dailyData.length) : 0} 건</div>
              <div>최소 :&nbsp;&nbsp;${dailyData.length > 0 ? Math.min(...dailyData.map(d => d.orders || 0)) : 0} 건</div>
            </div>
            ${dailyChartImage ? `<img src="${dailyChartImage}" style="width: 100%; height: 160px; object-fit: contain;" />` : '<div style="height: 160px; background: #f5f5f5; display: flex; align-items: center; justify-content: center; color: #666; font-size: 12px; text-align: center; padding: 20px;">일별 운송 현황 차트를 생성할 수 없습니다.<br/>데이터가 부족하거나 차트 렌더링에 문제가 발생했습니다.</div>'}
            <!-- 일별 캘린더 -->
            <div style="margin-top: 10px;">
              <!-- 요일 헤더 -->
              <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 1px; text-align: center; margin-bottom: 3px;">
                <div style="background: #f8f9fa; padding: 4px 2px; font-weight: 600; font-size: 9px; color: #666; border-radius: 2px;">일</div>
                <div style="background: #f8f9fa; padding: 4px 2px; font-weight: 600; font-size: 9px; color: #666; border-radius: 2px;">월</div>
                <div style="background: #f8f9fa; padding: 4px 2px; font-weight: 600; font-size: 9px; color: #666; border-radius: 2px;">화</div>
                <div style="background: #f8f9fa; padding: 4px 2px; font-weight: 600; font-size: 9px; color: #666; border-radius: 2px;">수</div>
                <div style="background: #f8f9fa; padding: 4px 2px; font-weight: 600; font-size: 9px; color: #666; border-radius: 2px;">목</div>
                <div style="background: #f8f9fa; padding: 4px 2px; font-weight: 600; font-size: 9px; color: #666; border-radius: 2px;">금</div>
                <div style="background: #f8f9fa; padding: 4px 2px; font-weight: 600; font-size: 9px; color: #666; border-radius: 2px;">토</div>
              </div>
              <!-- 캘린더 그리드 -->
              <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 1px; text-align: center;">
                ${(() => {
                  // 해당 월의 첫 번째 날의 요일 계산 (0: 일요일, 1: 월요일, ...)
                  const firstDay = new Date(selectedYear, selectedMonth - 1, 1).getDay();
                  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
                  
                  let calendarHtml = '';
                  
                  // 첫 주의 빈 칸들 추가
                  for (let i = 0; i < firstDay; i++) {
                    calendarHtml += '<div style="padding: 6px 2px;"></div>';
                  }
                  
                  // 실제 날짜들 추가
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(selectedYear, selectedMonth - 1, day);
                    const dayOfWeek = date.getDay();
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                    
                    // 해당 날짜의 운송 데이터 찾기
                    const dayData = dailyData.find(item => item.day === day);
                    const orders = dayData ? dayData.orders : 0;
                    
                    const backgroundColor = isWeekend ? '#f8f8f8' : 'white';
                    const textColor = orders > 15 ? '#d32f2f' : orders > 10 ? '#ff9800' : '#1976d2';
                    
                    calendarHtml += '<div style="background: ' + backgroundColor + '; padding: 4px 2px; border: 1px solid #ddd; border-radius: 2px; min-height: 22px; display: flex; flex-direction: column; justify-content: center;">' +
                      '<div style="font-size: 8px; color: #666; margin-bottom: 1px;">' + day + '</div>' +
                      '<div style="font-size: 9px; font-weight: 600; color: ' + textColor + ';">' + (orders ? orders + '건' : '') + '</div>' +
                      '</div>';
                  }
                  
                  return calendarHtml;
                })()}
              </div>
            </div>
          </div>

          <!-- 운송 거리 및 중량 분석 -->
          <div style="margin-bottom: 15px;">
            <h3 style="color: #333; font-size: 14px; margin-bottom: 8px; border-bottom: 2px solid ${customerInfo?.color || '#1976d2'}; padding-bottom: 4px;">• 운송 거리 및 중량 분석</h3>
            <div style="display: flex; justify-content: space-between;">
              <div style="width: 45%;">
                <div style="background: #f8f9fa; padding: 12px; border-radius: 4px;">
                  <table style="width: 100%; border-collapse: collapse;">
                    <tr style="background: #e9ecef;">
                      <th style="padding: 6px; text-align: left; border: 1px solid #dee2e6; font-size: 11px;">운송 거리 (km)</th>
                      <th style="padding: 6px; text-align: right; border: 1px solid #dee2e6; font-size: 11px;">거리</th>
                    </tr>
                    <tr>
                      <td style="padding: 5px; border: 1px solid #dee2e6; font-size: 10px;">평균거리</td>
                      <td style="padding: 5px; text-align: right; border: 1px solid #dee2e6; font-size: 10px;">${(reportData?.distance?.avg || 0).toLocaleString()}&nbsp;&nbsp;km</td>
                    </tr>
                    <tr>
                      <td style="padding: 5px; border: 1px solid #dee2e6; font-size: 10px;">최장거리</td>
                      <td style="padding: 5px; text-align: right; border: 1px solid #dee2e6; font-size: 10px;">${(reportData?.distance?.max || 0).toLocaleString()}&nbsp;&nbsp;km</td>
                    </tr>
                    <tr>
                      <td style="padding: 5px; border: 1px solid #dee2e6; font-size: 10px;">최단거리</td>
                      <td style="padding: 5px; text-align: right; border: 1px solid #dee2e6; font-size: 10px;">${(reportData?.distance?.min || 0).toLocaleString()}&nbsp;&nbsp;km</td>
                    </tr>
                  </table>
                </div>
              </div>
              <div style="width: 45%;">
                <div style="background: #f8f9fa; padding: 12px; border-radius: 4px;">
                  <table style="width: 100%; border-collapse: collapse;">
                    <tr style="background: #e9ecef;">
                      <th style="padding: 6px; text-align: left; border: 1px solid #dee2e6; font-size: 11px;">운송 중량 (Ton)</th>
                      <th style="padding: 6px; text-align: right; border: 1px solid #dee2e6; font-size: 11px;">중량</th>
                    </tr>
                    <tr>
                      <td style="padding: 5px; border: 1px solid #dee2e6; font-size: 10px;">평균 중량</td>
                      <td style="padding: 5px; text-align: right; border: 1px solid #dee2e6; font-size: 10px;">${(reportData?.weight?.avg || 0).toLocaleString()}&nbsp;&nbsp;Ton</td>
                    </tr>
                    <tr>
                      <td style="padding: 5px; border: 1px solid #dee2e6; font-size: 10px;">최대 중량</td>
                      <td style="padding: 5px; text-align: right; border: 1px solid #dee2e6; font-size: 10px;">${(reportData?.weight?.max || 0).toLocaleString()}&nbsp;&nbsp;Ton</td>
                    </tr>
                    <tr>
                      <td style="padding: 5px; border: 1px solid #dee2e6; font-size: 10px;">총 운송량</td>
                      <td style="padding: 5px; text-align: right; border: 1px solid #dee2e6; font-size: 10px;">${(reportData?.weight?.total || 0).toLocaleString()}&nbsp;&nbsp;Ton</td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- 차종별 현황 차트 -->
          <div style="margin-bottom: 15px;">
            <h3 style="color: #333; font-size: 14px; margin-bottom: 8px; border-bottom: 2px solid ${customerInfo?.color || '#1976d2'}; padding-bottom: 4px;">• 운송 차량 및 중량</h3>
            <div style="display: flex;">
              <div style="width: 55%;">
                ${vehicleChartImage ? `<img src="${vehicleChartImage}" style="width: 100%; height: 180px; object-fit: contain;" />` : '<div style="height: 180px; background: #f5f5f5; display: flex; align-items: center; justify-content: center; color: #666; font-size: 12px; text-align: center; padding: 20px;">차종별 현황 차트를 생성할 수 없습니다.<br/>데이터가 부족하거나 차트 렌더링에 문제가 발생했습니다.</div>'}
              </div>
              <div style="width: 45%; padding-left: 15px;">
                <div style="background: #f8f9fa; padding: 8px; border-radius: 4px; min-height: 0;">
                  ${vehicleTypeData.length > 0 ? vehicleTypeData.slice(0, 5).map(item => `
                    <div style="display: flex; justify-content: space-between; padding: 3px 0; border-bottom: 1px solid #e0e0e0;">
                      <span style="font-size: 11px;">${item?.name || '미분류'}</span>
                      <span style="font-size: 11px; font-weight: 600; color: ${customerInfo?.color || '#1976d2'};">${(item?.value || 0).toLocaleString()}건</span>
                    </div>
                  `).join('') : '<div style="padding: 20px; text-align: center; color: #666; font-size: 11px;">차종별 데이터가 없습니다.</div>'}
                </div>
              </div>
            </div>
          </div>

          <!-- 푸터: 항상 하단 고정 -->
          <div style="position: absolute; bottom: 8px; left: 0; width: 100%;">
            <div style="text-align:center; font-size:12px; color:#444444; font-weight:700;">성우로지스(주)</div>
            <div style="text-align:center; font-size:12px; color:#888; margin-top:2px;">Your Cargo, Our Care</div>
          </div>
        </div>
      `;

      // 4. PDF 생성
      console.log('PDF 렌더링 중...');
      await html2pdf().set({
        margin: [8, 8, 8, 8],
        filename: `${customerInfo?.name || '화주'}_운송현황보고서_${selectedYear}년${selectedMonth}월.pdf`,
        html2canvas: { 
          scale: 2.5,
          useCORS: true,
          letterRendering: true,
          allowTaint: true,
          dpi: 300,
          logging: false,
          backgroundColor: '#ffffff',
          width: 794,
          height: 1123,
          pixelRatio: 2
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait',
          compress: true
        }
      }).from(element).save();

      console.log('PDF 생성 완료');

    } catch (error) {
      console.error('PDF 생성 중 오류:', error);
      
      // 구체적인 오류 메시지 제공
      let errorMessage = 'PDF 생성 중 오류가 발생했습니다.';
      if (error instanceof Error) {
        if (error.message.includes('html2canvas')) {
          errorMessage = 'PDF 생성 중 페이지 렌더링에 실패했습니다. 잠시 후 다시 시도해주세요.';
        } else if (error.message.includes('jsPDF')) {
          errorMessage = 'PDF 파일 생성에 실패했습니다. 브라우저 설정을 확인해주세요.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'PDF 생성 시간이 초과되었습니다. 데이터량을 줄이거나 잠시 후 다시 시도해주세요.';
        }
      }
      
      alert(errorMessage + '\n\n상세 오류: ' + (error instanceof Error ? error.message : '알 수 없는 오류'));
    } finally {
      setPdfGenerating(false);
    }
  };

  // 차트를 캔버스로 변환하는 헬퍼 함수 (안정성 개선)
  const convertChartToCanvas = async (chartId: string): Promise<HTMLCanvasElement | null> => {
    try {
      const chartElement = document.getElementById(chartId);
      if (!chartElement) {
        console.warn(`차트 요소를 찾을 수 없습니다: ${chartId}`);
        return null;
      }

      // recharts svg를 canvas로 변환
      const svgElement = chartElement.querySelector('svg');
      if (!svgElement) {
        console.warn(`SVG 요소를 찾을 수 없습니다: ${chartId}`);
        return null;
      }

      // SVG 크기 정보 확인
      const svgRect = svgElement.getBoundingClientRect();
      const svgWidth = svgRect.width || parseInt(svgElement.getAttribute('width') || '600');
      const svgHeight = svgRect.height || parseInt(svgElement.getAttribute('height') || '300');

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Canvas 컨텍스트를 가져올 수 없습니다');
        return null;
      }

      // 고해상도를 위한 스케일링
      const scale = 2;
      canvas.width = svgWidth * scale;
      canvas.height = svgHeight * scale;
      canvas.style.width = svgWidth + 'px';
      canvas.style.height = svgHeight + 'px';
      ctx.scale(scale, scale);

      // SVG 데이터 직렬화 (폰트 정보 보존)
      const svgClone = svgElement.cloneNode(true) as SVGElement;
      
      // 폰트 정보 명시적 설정 및 크기 증가
      const allTexts = svgClone.querySelectorAll('text');
      allTexts.forEach(text => {
        if (!text.style.fontFamily) {
          text.style.fontFamily = 'Arial, sans-serif';
        }
        
        // 기존 폰트 크기 확인 후 더 크게 설정
        const currentFontSize = text.style.fontSize || text.getAttribute('font-size') || '12px';
        const sizeValue = parseInt(currentFontSize);
        
        // PDF용 폰트 크기 조정 (적절한 크기로)
        if (chartId === 'vehicle-chart') {
          text.style.fontSize = '15px'; // 도넛차트 라벨을 조금 더 크게
          text.style.fontWeight = '600'; // 적당한 굵기
        } else {
          text.style.fontSize = '10px'; // 일별 차트는 기존 크기 유지
          text.style.fontWeight = '600';
        }
      });

      const svgData = new XMLSerializer().serializeToString(svgClone);
      
      // 브라우저 호환성을 위한 Base64 인코딩 개선
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      
      const img = new Image();
      
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          console.error(`차트 변환 타임아웃: ${chartId}`);
          URL.revokeObjectURL(url);
          resolve(null);
        }, 5000); // 5초 타임아웃

        img.onload = () => {
          clearTimeout(timeout);
          try {
            // 흰색 배경 먼저 그리기
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, svgWidth, svgHeight);
            
            // 이미지 그리기
            ctx.drawImage(img, 0, 0, svgWidth, svgHeight);
            
            URL.revokeObjectURL(url);
            resolve(canvas);
          } catch (drawError) {
            console.error('Canvas 그리기 오류:', drawError);
            URL.revokeObjectURL(url);
            resolve(null);
          }
        };
        
        img.onerror = (error) => {
          clearTimeout(timeout);
          console.error('이미지 로드 오류:', error);
          URL.revokeObjectURL(url);
          resolve(null);
        };
        
        img.src = url;
      });
    } catch (error) {
      console.error('차트 변환 오류:', error);
      return null;
    }
  };

  if (!customerInfo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, width: '100vw', height: '100vh',
        background: 'rgba(255,255,255,0.7)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <CircularProgress size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: isMobile ? 2 : 4, 
      pb: isMobile ? 10 : 4, // 모바일에서 하단 네비게이션 바 높이만큼 패딩 추가
      bgcolor: '#f9fafb', 
      minHeight: '100vh' 
    }}>
      {/* 헤더 */}
      <Paper sx={{ p: isMobile ? 2 : 4, mb: 4, bgcolor: customerInfo.backgroundColor, position: 'relative' }}>
        {/* 모바일과 PC 레이아웃 분리 */}
        {isMobile ? (
          /* 모바일 레이아웃 */
          <Box>
            {/* 보고서 제목 */}
            <Typography variant="h6" fontWeight={700} sx={{ 
              color: customerInfo.color, 
              mb: 3, 
              textAlign: 'center',
              lineHeight: 1.3
            }}>
              {selectedYear}년 {selectedMonth}월<br />
              운송 현황 보고서
            </Typography>
            
            {/* 컨트롤 영역 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* 년도/월 선택 */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <FormControl sx={{ minWidth: 100 }}>
                  <Select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value as number)}
                    sx={{ bgcolor: 'white', height: 48 }}
                  >
                    {years.map(year => (
                      <MenuItem key={year} value={year}>{year}년</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl sx={{ minWidth: 90 }}>
                  <Select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value as number)}
                    sx={{ bgcolor: 'white', height: 48 }}
                  >
                    {months.map(month => (
                      <MenuItem key={month} value={month}>{month}월</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              {/* PDF 다운로드 버튼 - 모바일에서는 숨김 */}
              {!isMobile && (
                <Button
                  variant="contained"
                  startIcon={pdfGenerating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  onClick={handleDownloadPDF}
                  disabled={pdfGenerating}
                  sx={{ 
                    bgcolor: customerInfo.color,
                    '&:hover': { bgcolor: `${customerInfo.color}dd` },
                    height: 48,
                    fontSize: 16,
                    fontWeight: 600,
                    alignSelf: 'center'
                  }}
                >
                  {pdfGenerating ? 'PDF 생성중...' : 'PDF'}
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          /* PC 레이아웃 - 로고 부분도 간소화 */
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: customerInfo.color }}>
                {selectedYear}년 {selectedMonth}월 운송 현황 보고서
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* 년/월 선택 */}
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value as number)}
                  sx={{ bgcolor: 'white' }}
                >
                  {years.map(year => (
                    <MenuItem key={year} value={year}>{year}년</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ minWidth: 70 }}>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value as number)}
                  sx={{ bgcolor: 'white' }}
                >
                  {months.map(month => (
                    <MenuItem key={month} value={month}>{month}월</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {!isMobile && (
                <Button
                  variant="contained"
                  startIcon={pdfGenerating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  onClick={handleDownloadPDF}
                  disabled={pdfGenerating}
                  sx={{ 
                    bgcolor: customerInfo.color,
                    '&:hover': { bgcolor: `${customerInfo.color}dd` }
                  }}
                >
                  {pdfGenerating ? 'PDF 생성중...' : 'PDF'}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Paper>

      {/* 주요 통계 카드 */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
        gap: 2,
        mb: 4
      }}>
        <StatCard
          title="총 운송 건수"
          value={monthlyStats.totalOrders}
          unit="건"
          change={monthlyStats.previousMonthChange.orders}
          isIncrease={monthlyStats.previousMonthChange.orders > 0}
          icon={<LocalShippingIcon />}
          color={customerInfo.color}
        />
        
        <StatCard
          title="총 운송량"
          value={monthlyStats.totalWeight}
          unit="Ton"
          change={monthlyStats.previousMonthChange.weight}
          isIncrease={monthlyStats.previousMonthChange.weight > 0}
          icon={<ScaleIcon />}
          color={customerInfo.color}
        />
        
        {/* 운송료 통계는 showStatsInReport가 true일 때만 노출 */}
        {showStatsInReport && (
          <StatCard
            title="총 운송료"
            value={Math.floor(monthlyStats.totalAmount / 10000)}
            unit="만원"
            change={monthlyStats.previousMonthChange.amount}
            isIncrease={monthlyStats.previousMonthChange.amount > 0}
            icon={<AttachMoneyIcon />}
            color={customerInfo.color}
          />
        )}
        
        <StatCard
          title="총 운송 거리"
          value={monthlyStats.totalDistance}
          unit="km"
          change={monthlyStats.previousMonthChange.distance}
          isIncrease={monthlyStats.previousMonthChange.distance > 0}
          icon={<TrendingUpIcon />}
          color={customerInfo.color}
        />
      </Box>

      {/* 운송 거리 및 중량 분석 */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(6, 1fr)',
        gap: 2,
        mb: 4
      }}>
        {/* 평균 거리 */}
        <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                bgcolor: `${customerInfo.color}20`,
                color: customerInfo.color,
                mr: 2
              }}>
                <TrendingUpIcon />
              </Box>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  fontSize: isMobile ? 12 : 14,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                평균 거리
              </Typography>
            </Box>
            
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1, color: '#333' }}>
              {reportData?.distance?.avg || '0'}
              <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1 }}>
                km
              </Typography>
            </Typography>
          </CardContent>
        </Card>

        {/* 최장 거리 */}
        <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                bgcolor: `${customerInfo.color}20`,
                color: customerInfo.color,
                mr: 2
              }}>
                <TrendingUpIcon />
              </Box>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  fontSize: isMobile ? 12 : 14,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                최장 거리
              </Typography>
            </Box>
            
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1, color: '#333' }}>
              {reportData?.distance?.max || '0'}
              <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1 }}>
                km
              </Typography>
            </Typography>
          </CardContent>
        </Card>

        {/* 최단 거리 */}
        <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                bgcolor: `${customerInfo.color}20`,
                color: customerInfo.color,
                mr: 2
              }}>
                <TrendingUpIcon />
              </Box>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  fontSize: isMobile ? 12 : 14,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                최단 거리
              </Typography>
            </Box>
            
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1, color: '#333' }}>
              {reportData?.distance?.min || '0'}
              <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1 }}>
                km
              </Typography>
            </Typography>
          </CardContent>
        </Card>

        {/* 평균 중량 */}
        <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                bgcolor: `${customerInfo.color}20`,
                color: customerInfo.color,
                mr: 2
              }}>
                <ScaleIcon />
              </Box>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  fontSize: isMobile ? 12 : 14,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                평균 중량
              </Typography>
            </Box>
            
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1, color: '#333' }}>
              {reportData?.weight?.avg || '0'}
              <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1 }}>
                Ton
              </Typography>
            </Typography>
          </CardContent>
        </Card>

        {/* 최대 중량 */}
        <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                bgcolor: `${customerInfo.color}20`,
                color: customerInfo.color,
                mr: 2
              }}>
                <ScaleIcon />
              </Box>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  fontSize: isMobile ? 12 : 14,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                최대 중량
              </Typography>
            </Box>
            
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1, color: '#333' }}>
              {reportData?.weight?.max || '0'}
              <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1 }}>
                Ton
              </Typography>
            </Typography>
          </CardContent>
        </Card>

        {/* 총 운송량 */}
        <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                bgcolor: `${customerInfo.color}20`,
                color: customerInfo.color,
                mr: 2
              }}>
                <ScaleIcon />
              </Box>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ 
                  fontSize: isMobile ? 12 : 14,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {isMobile ? '총 운송량' : '총 운송량'}
              </Typography>
            </Box>
            
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1, color: '#333' }}>
              {reportData?.weight?.total || '0'}
              <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1 }}>
                Ton
              </Typography>
            </Typography>
          </CardContent>
                 </Card>
      </Box>

      {/* 차트 영역 */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
        gap: 3,
        mb: 4
      }}>
        {/* 일별 운송 현황 차트 */}
        <Paper sx={{ p: 3, height: isMobile ? 300 : 400 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: customerInfo.color }}>
            일별 운송 현황
          </Typography>
          <div id="daily-chart">
            <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip 
                  formatter={(value: any) => [`${value}건`, '오더개수']}
                  labelStyle={{ color: '#333' }}
                />
                <Bar dataKey="orders" fill={customerInfo.color} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Paper>

        {/* 차종별 현황 */}
        <Paper sx={{ p: 3, height: isMobile ? 300 : 400 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: customerInfo.color }}>
            차종별 운송 현황
          </Typography>
          
          <div id="vehicle-chart">
            <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
              <PieChart>
                <Pie
                  data={vehicleTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ cx, cy, midAngle, innerRadius, outerRadius, name, value, percent }) => {
                    // 5% 미만인 항목은 라벨을 표시하지 않음
                    if (percent < 0.05) return null;
                    
                    const RADIAN = Math.PI / 180;
                    const radius = outerRadius + 35; // 라벨 위치를 좀 더 바깥쪽으로
                    const x = cx + radius * Math.cos(-midAngle * RADIAN);
                    const y = cy + radius * Math.sin(-midAngle * RADIAN);
                    
                    return (
                      <text 
                        x={x} 
                        y={y} 
                        fill="#333" 
                        textAnchor={x > cx ? 'start' : 'end'} 
                        dominantBaseline="central"
                        fontSize={isMobile ? "16" : "18"} // 모바일: 16px, PC: 18px로 증가
                        fontWeight="700" // 더 굵게
                        style={{ textShadow: '1px 1px 2px rgba(255,255,255,0.8)' }} // 가독성을 위한 그림자
                      >
                        {`${name}: ${value}건`}
                      </text>
                    );
                  }}
                  outerRadius={isMobile ? 50 : 85}
                  innerRadius={isMobile ? 25 : 40}
                  fill="#8884d8"
                  dataKey="value"
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {vehicleTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: any) => [`${value}건`, '운송량']}
                  labelStyle={{ color: '#333', fontSize: '14px', fontWeight: '600' }}
                  contentStyle={{ fontSize: '14px', fontWeight: '600' }}
                />
              </PieChart>
            </ResponsiveContainer>
            
            {/* 범례 추가 */}
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {vehicleTypeData.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      bgcolor: item.color, 
                      borderRadius: '2px' 
                    }} />
                    <Typography variant="caption" sx={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '600' }}>
                      {item.name}: {item.value}건
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </div>
        </Paper>
      </Box>

      {/* 일별 캘린더 현황 */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box>
          {/* 요일 헤더 */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(7, 1fr)', 
            gap: 1, 
            textAlign: 'center', 
            mb: 1
          }}>
            {['일', '월', '화', '수', '목', '금', '토'].map((day, index) => (
              <Box 
                key={day}
                sx={{ 
                  bgcolor: '#f8f9fa', 
                  p: 1, 
                  fontWeight: 600, 
                  fontSize: 14, 
                  color: '#666', 
                  borderRadius: 1,
                  border: '1px solid #e0e0e0'
                }}
              >
                {day}
              </Box>
            ))}
          </Box>
          
          {/* 캘린더 그리드 */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(7, 1fr)', 
            gap: 1, 
            textAlign: 'center'
          }}>
            {(() => {
              // 해당 월의 첫 번째 날의 요일 계산 (0: 일요일, 1: 월요일, ...)
              const firstDay = new Date(selectedYear, selectedMonth - 1, 1).getDay();
              const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
              
              const calendarItems: any[] = [];
              
              // 첫 주의 빈 칸들 추가
              for (let i = 0; i < firstDay; i++) {
                calendarItems.push(
                  <Box key={`empty-${i}`} sx={{ p: 2 }} />
                );
              }
              
              // 실제 날짜들 추가
              for (let day = 1; day <= daysInMonth; day++) {
                const date = new Date(selectedYear, selectedMonth - 1, day);
                const dayOfWeek = date.getDay();
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                
                // 해당 날짜의 운송 데이터 찾기
                const dayData = dailyData.find(item => item.day === day);
                const orders = dayData ? dayData.orders : 0;
                
                const backgroundColor = isWeekend ? '#f8f8f8' : 'white';
                const textColor = orders > 15 ? '#d32f2f' : orders > 10 ? '#ff9800' : '#1976d2';
                
                calendarItems.push(
                  <Box 
                    key={day}
                    sx={{ 
                      bgcolor: backgroundColor, 
                      p: 1, 
                      border: '1px solid #ddd', 
                      borderRadius: 1, 
                      minHeight: 60,
                      display: 'flex', 
                      flexDirection: 'column', 
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      {day}
                    </Typography>
                    {orders > 0 && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600, 
                          color: textColor,
                          fontSize: 12
                        }}
                      >
                        {orders}건
                      </Typography>
                    )}
                  </Box>
                );
              }
              
              return calendarItems;
            })()}
          </Box>
        </Box>
      </Paper>

      {/* 푸터 */}
      <Box sx={{ textAlign: 'center', mt: 6, py: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: customerInfo.color }}>
          성우로지스(주)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Your Cargo, Our Care
        </Typography>
      </Box>
    </Box>
  );
};

export default CustomerReportPage; 